import { useMemo, useEffect, JSX } from "react";
import { Modal } from "../../components/Modal";
import { useCardStatusStore } from "../../stores/cardStatusStore";
import { useKycStore } from "../../stores/useKycStore";
import KycPendingViaRain from "./KycPendingViaRain";
import PreRainTerms from "./PreRainTerms";
import { SumSubWidget } from "../../components/SumSubWidget";
import KycRejectedViaRain from "./KycRejectedViaRain";
import KycModalWrapper from "./KycModalWrapper";
import SuccessKycSvg from "../../components/icons/SuccessKycSvg";
import PendingKycSvg from "../../components/icons/PendingKycSvg";
import RejectedSvg from "../../components/icons/RejectedSvg";
import ResubmissionKycIcon from "../../components/icons/ResubmissionKycIcon";

interface KycModalProps {
  eoaAddress: string;
  onClose?: () => void;
  onComplete?: () => void;
}
export function KycModal({
  eoaAddress,
  onClose,
  onComplete,
}: KycModalProps): JSX.Element | null {
  const { status } = useCardStatusStore();
  const { acceptedRainTerms } = useKycStore();

  // Call onComplete when status becomes approved
  useEffect(() => {
    if (status === "approved" && onComplete) {
      onComplete();
    }
  }, [status, onComplete]);

  const getModalView = useMemo(() => {
    switch (status) {
      case "userNotExisting": {
        return {
          title: "User Verification",
          component: <SumSubWidget eoaAddress={eoaAddress} />,
        };
      }

      case "pending": {
        if (acceptedRainTerms) {
          return {
            title: "User Verification is in Process",
            component: <KycPendingViaRain />,
          };
        } else {
          return {
            title: "User Verification is in Process",
            component: (
              <KycModalWrapper
                icon={<PendingKycSvg />}
                text="Your verification is being processed and may take up to 2 days to complete. Please check back later for issuance."
                buttonText="Dashboard"
                onButtonClick={onClose}
              />
            ),
          };
        }
      }

      case "rejected": {
        return {
          title: "User Verification Rejected",
          component: (
            <KycModalWrapper
              icon={<RejectedSvg />}
              text="Your User Verification was rejected. Please contact support to resolve the issue."
              buttonText="Contact Support"
              onButtonClick={onClose}
            />
          ),
        };
      }

      case "resubmit": {
        return {
          title: "User Verification is in Process",
          component: (
            <KycModalWrapper
              icon={<ResubmissionKycIcon />}
              text="Your verification with was rejected. Please Contact Support to resolve the issue."
              buttonText="Contact Support"
              onButtonClick={onClose}
            />
          ),
        };
      }
      case "submitted": {
        return {
          title: "User Verification Submitted",
          component: (
            <KycModalWrapper
              icon={<SuccessKycSvg />}
              text="Your User Verification has been successfully submitted. You can close this window and return to the dashboard. Once the User Verification is approved, you'll be able to start using your card."
              buttonText="Dashboard"
              onButtonClick={onClose}
            />
          ),
        };
      }

      case "approved": {
        // For approved status, we just return null as this SDK doesn't handle card activation
        return {
          title: "",
          component: <></>,
        };
      }

      default:
        return {
          title: "Apply for Card",
          component: <></>,
        };
    }
  }, [status, acceptedRainTerms]);

  const { title, component } = getModalView;

  // Don't render modal if status is approved or no component
  if (status === "approved" || !component) {
    return null;
  }

  return (
    <Modal title={title} onClose={onClose || (() => {})} padding="p-1">
      {component}
    </Modal>
  );
}
